import { apiFetch } from "../../apiClient";
import { parseErrorResponse } from "../../utils/apiError";
import { checkAuthStatus } from "../../utils/authStatus";
import { FEEDBACK_FAILURE_MESSAGE } from "./constants";
import type {
  OutputFeedbackOutputType,
  OutputFeedbackRating,
  OutputFeedbackTarget,
  OutputFeedbackValue,
} from "./types";

const FEEDBACK_PATH = "/api/finster/feedback";

export interface OutputFeedbackClientError {
  message: string;
  status: number;
}

export type OutputFeedbackResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: OutputFeedbackClientError };

export function isFeedbackErr<T>(
  result: OutputFeedbackResult<T>
): result is { ok: false; error: OutputFeedbackClientError } {
  return !result.ok;
}

function definedEntries(properties: Record<string, unknown>) {
  return Object.fromEntries(
    Object.entries(properties).filter(([, value]) => value !== undefined)
  );
}

/**
 * PUT body for `/api/v4/feedback`. Only fields the upstream schema accepts for
 * this `outputType` are included — spreading the UI target would send
 * Amplitude-only context (e.g. `artifactKind` on `chat_response`) and 400.
 */
export function buildFeedbackRequestBody({
  target,
  value,
  reasons,
}: {
  target: OutputFeedbackTarget;
  value: OutputFeedbackValue;
  reasons: string[];
}): Record<string, unknown> {
  const shared = {
    outputId: target.outputId,
    outputType: target.outputType,
    surface: target.surface,
    sessionId: target.sessionId,
    value,
    reasons,
  };

  if (target.outputType === "chat_response") {
    return definedEntries({
      ...shared,
      queryId: target.queryId || target.outputId,
    });
  }

  if (target.outputType === "chat_artifact") {
    return definedEntries({
      ...shared,
      queryId: target.queryId,
      artifactKind: target.artifactKind,
      artifactVersion: target.artifactVersion,
    });
  }

  return definedEntries({
    ...shared,
    artifactKind: target.artifactKind,
    taskId: target.taskId,
    taskRunId: target.taskRunId,
  });
}

function asString(value: unknown): string | undefined {
  return typeof value === "string" && value.trim() ? value.trim() : undefined;
}

function asReasons(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string" && item.length > 0);
}

function asOutputType(value: unknown): OutputFeedbackOutputType | undefined {
  if (value === "chat_response" || value === "chat_artifact" || value === "task_output") {
    return value;
  }
  return undefined;
}

function asValue(value: unknown): OutputFeedbackValue | undefined {
  if (value === "positive" || value === "negative") return value;
  return undefined;
}

function parseRating(data: unknown): OutputFeedbackRating | null {
  if (!data || typeof data !== "object") return null;
  const record = data as Record<string, unknown>;
  const outputId = asString(record.outputId ?? record.output_id);
  const outputType = asOutputType(record.outputType ?? record.output_type);
  const value = asValue(record.value ?? record.feedback_value);
  if (!outputId || !outputType || !value) return null;

  return {
    outputId,
    outputType,
    value,
    reasons: asReasons(record.reasons ?? record.feedback_reasons),
  };
}

async function parseError(
  response: Response,
  fallbackMessage: string
): Promise<OutputFeedbackClientError> {
  return {
    message: (await parseErrorResponse(response)) || fallbackMessage,
    status: response.status,
  };
}

async function attempt<T>(
  request: () => Promise<OutputFeedbackResult<T>>,
  fallbackMessage: string
): Promise<OutputFeedbackResult<T>> {
  try {
    return await request();
  } catch {
    return { ok: false, error: { message: fallbackMessage, status: 0 } };
  }
}

/** Ratings the caller already left in a chat session. */
export async function listOutputFeedback(scope: {
  sessionId?: string;
}): Promise<OutputFeedbackResult<OutputFeedbackRating[]>> {
  return attempt(async () => {
    const params = new URLSearchParams();
    if (scope.sessionId) params.set("session_id", scope.sessionId);
    const query = params.toString();

    const response = await apiFetch(`${FEEDBACK_PATH}${query ? `?${query}` : ""}`);
    checkAuthStatus(response);

    if (!response.ok) {
      return { ok: false, error: await parseError(response, "Failed to load feedback") };
    }

    const data = (await response.json()) as { items?: unknown };
    const items = Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : [];
    return {
      ok: true,
      data: items
        .map((item) => parseRating(item))
        .filter((item): item is OutputFeedbackRating => item !== null),
    };
  }, "Failed to load feedback");
}

export async function submitOutputFeedback({
  target,
  value,
  reasons,
}: {
  target: OutputFeedbackTarget;
  value: OutputFeedbackValue;
  reasons: string[];
}): Promise<OutputFeedbackResult<OutputFeedbackRating>> {
  return attempt(async () => {
    const response = await apiFetch(FEEDBACK_PATH, {
      method: "PUT",
      body: JSON.stringify(buildFeedbackRequestBody({ target, value, reasons })),
    });
    checkAuthStatus(response);

    if (!response.ok) {
      return { ok: false, error: await parseError(response, "Failed to submit feedback") };
    }

    const parsed = parseRating(await response.json());
    return {
      ok: true,
      data:
        parsed ?? {
          outputId: target.outputId,
          outputType: target.outputType,
          value,
          reasons,
        },
    };
  }, FEEDBACK_FAILURE_MESSAGE);
}

export async function removeOutputFeedback(
  target: Pick<OutputFeedbackTarget, "outputId" | "outputType">
): Promise<OutputFeedbackResult<void>> {
  return attempt(async () => {
    const response = await apiFetch(FEEDBACK_PATH, {
      method: "DELETE",
      body: JSON.stringify({
        outputId: target.outputId,
        outputType: target.outputType,
      }),
    });
    checkAuthStatus(response);

    if (!response.ok) {
      return { ok: false, error: await parseError(response, "Failed to remove feedback") };
    }

    return { ok: true, data: undefined };
  }, FEEDBACK_FAILURE_MESSAGE);
}
